"use client";
import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";

import {
  type AddQuoteResult,
  type GlobalContentPage,
  type HouseholderCommonPage,
  type HouseholderQuote,
  HouseholderType,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { ComposedDropdownSelect } from "@/components/shared/composedDropdownSelect/ComposedDropdownSelect";
import { ComposedInputField } from "@/components/shared/composedInputField/ComposedInputField";
import {
  ComposedRadioButton,
  ComposedRadioButtonValue,
} from "@/components/shared/composedRadioButton/ComposedRadioButton";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import type { HouseholderEntryContent } from "@/services/cms/contentTypes/entry/HouseholderEntryContent";
import { useEntryContent } from "@/services/cms/tanstackQuery/hooks/useEntryContent";
import { HouseholderEntryValidationSchema } from "@/validators/products/householder/HouseholderEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: HouseholderEntryContent;
  externalCommonContent?: HouseholderCommonPage | null;
  externalGlobalContent?: GlobalContentPage | null;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: HouseholderQuote) => Promise<AddQuoteResult>;
};

export const HouseholderEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
  externalCommonContent,
  externalGlobalContent,
}: EntryProps): React.JSX.Element => {
  const {
    currentUser,
    currentPolicyHolder,
    siteContent,
    isLoading,
    submitHandler,
  } = useEntry<HouseholderQuote, HouseholderEntryContent>({
    externalUser,
    externalSubmitHandler,
    externalIsLoading,
    externalContent,
    externalPolicyHolder,
    fetchSiteContent: !externalCommonContent || !externalGlobalContent,
  });
  const { data: householderEntryPage } =
    useEntryContent<typeof ProductType.HOUSEHOLDER>(externalContent);
  const householderCommonPage =
    externalCommonContent ?? siteContent?.householderCommonPage;
  const housingCompanyValues =
    householderEntryPage?.housingCompany?.RadionButtonValues ?? [];
  const housingCompanyYesOption = housingCompanyValues[0];
  const housingCompanyNoOption = housingCompanyValues[1];
  const defaultValues: HouseholderQuote = {
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
    isHousingCompany: true,
    productType: ProductType.HOUSEHOLDER,
    householder: {
      livingSpace: undefined,
      householderType: HouseholderType.APARTMENT,
    },
  };
  const { handleSubmit, control, formState, watch } = useForm<HouseholderQuote>(
    {
      defaultValues,
      resolver: yupResolver(
        HouseholderEntryValidationSchema(householderEntryPage),
      ),
      disabled: isLoading,
    },
  );

  const householderType = watch("householder.householderType");
  const { errors } = formState;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          noValidate
          onSubmit={handleSubmit(submitHandler)}
        >
          <Controller
            control={control}
            name="householder.householderType"
            render={({ field }) => (
              <ComposedDropdownSelect
                id="householder.householderType"
                errorMessage={errors?.householder?.householderType?.message}
                label={householderEntryPage?.housingType?.Label ?? ""}
                placeholder={
                  householderEntryPage?.housingType?.Placeholder ?? undefined
                }
                {...field}
                value={field.value ?? ""}
              >
                <option value={HouseholderType.APARTMENT}>
                  {householderCommonPage?.householderTypeApartmentLabel?.value}
                </option>
                <option value={HouseholderType.ROW_HOUSE}>
                  {householderCommonPage?.householderTypeRowHouseLabel?.value}
                </option>
                <option value={HouseholderType.PAIR_HOUSE}>
                  {householderCommonPage?.householderTypePairHouseLabel?.value}
                </option>
              </ComposedDropdownSelect>
            )}
          />
          <Controller
            control={control}
            name="householder.livingSpace"
            render={({ field }) => (
              <ComposedInputField
                id="householder.livingSpace"
                errorMessage={errors?.householder?.livingSpace?.message}
                dataTestid="living-space-square-footage-input"
                popoverMessage={householderEntryPage?.livingSpace?.Popover}
                placeholder={
                  householderEntryPage?.livingSpace?.Placeholder ?? undefined
                }
                {...field}
                value={field.value ?? ""}
                type={"number"}
              >
                {householderEntryPage?.livingSpace?.Label}
              </ComposedInputField>
            )}
          />
          <Controller
            control={control}
            name="policyHolderSsn"
            render={({ field }) => (
              <SsnInput
                disabled={
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated ||
                  isLoading
                }
                id="policyHolderSsn"
                dataTestid="policy-holder-ssn-input"
                placeholder={
                  householderEntryPage?.policyHolderSsn?.Placeholder ??
                  undefined
                }
                popoverMessage={
                  householderEntryPage?.policyHolderSsn?.Popover ?? ""
                }
                errorMessage={errors?.policyHolderSsn?.message}
                {...field}
              >
                {householderEntryPage?.policyHolderSsn?.Label}
              </SsnInput>
            )}
          />
          {householderType === HouseholderType.PAIR_HOUSE && (
            <Controller
              control={control}
              name="isHousingCompany"
              render={({ field }) => (
                <ComposedRadioButton
                  disabled={isLoading}
                  id="isHousingCompany"
                  label={
                    householderEntryPage?.housingCompany?.Label ??
                    "isHousingCompany"
                  }
                  errorMessage={errors?.isHousingCompany?.message}
                  popoverMessage={householderEntryPage?.housingCompany?.Popover}
                  invalid={undefined}
                  {...field}
                  value={field.value ? "true" : "false"}
                  onChange={(e) => field.onChange(e.target.value === "true")}
                >
                  <ComposedRadioButtonValue
                    disabled={isLoading}
                    defaultChecked={true}
                    name="isHousingCompany"
                    id="isHousingCompany.Yes"
                    label={housingCompanyYesOption?.Label}
                    value={housingCompanyYesOption?.Value ?? "true"}
                  />
                  <ComposedRadioButtonValue
                    disabled={isLoading}
                    name="isHousingCompany"
                    id="isHousingCompany.No"
                    label={housingCompanyNoOption?.Label}
                    value={housingCompanyNoOption?.Value ?? "false"}
                  />
                </ComposedRadioButton>
              )}
            />
          )}
          <ProductContainer.CallToAction
            htmlType="submit"
            loading={isLoading}
            disabled={isLoading}
            data-testid="householder-entry-submit-button"
          >
            {householderEntryPage?.submitButton?.Label ?? "Katso hinta"}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
